import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Chip,
  Divider,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScoreIcon from "@mui/icons-material/Score";
import TableChartIcon from "@mui/icons-material/TableChart";
import "./Scores.css";
import { useNavigate } from "react-router-dom";

const ScoresTemplate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const navigate = useNavigate();

  // Google Sheets configuration for each category
  const categorySheets = [
    {
      name: "Bowling",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vTy9BRsWn4x6S3eWTAa7NDQotlKPKWdki2cdR46umJNlQpNcFyYjzWCaxrI4w2l3Q/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Follow Line Enhanced",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vQeCy2rsBii3AO6HSAyCfMyVxFE1L0wrbheMF1sAvrD6PsGO8BVz-G2vsNW1jcgcQ/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Follow Line Lego",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vQympCFMy7NI97ZbtHgxCnTwPAl72TxODd1uRkMU6QnMY0afuMpovfwSD-CJaoywg/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Follow Line Open",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vQG7eISBW_ptiQOmOhemnZQLX5TENXmu5xpFLoKzjCXc6J1Qa8l9wfGai8f59RvYw/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "RoboGolf",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vS_tQs5reyuSg4bE_MUL7iLO2kBWSnifUYxHooTXpTnOFrvmUCUCjYeqJMltWstSg/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "RoboBoom Challenge",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vTQe-e5JwF0IMHEPCpEA43ZscgkFCQmA2NxaFtO-oo8D0H5n-YU2WBvmoA3HnDuxA/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "RoboBoom Curling",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vRsyDZ1CjJKmDjbmlMjQmh9-Q4GHpJP7cNSSl5NZ9z1NJzbrfOm66AuSzjpBFuqSw/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "RoboBoom Line Sprint",
      link: "https://challonge.com/RoboBoom_Line_sprint_IRC_2026",
    },
    {
      name: "Sumo Lego Junior",
      link: "https://challonge.com/Sumo_Lego_IRC_2026",
    },
    {
      name: "Sumo Lego Senior",
      link: "https://challonge.com/irnzezlw",
    },
    {
      name: "Sumo Mega",
      link: "https://challonge.com/Sumo_Mega_IRC_2026",
    },
    { name: "Sumo Mini", link: "https://challonge.com/Sumo_Mini_IRC_2026" },
  ];

  useEffect(() => {
    loadScores();
  }, []);

  const loadScores = async () => {
    setLoading(true);
    setError(null);

    try {
      // Simulate loading delay
      await new Promise((resolve) => setTimeout(resolve, 500));
      setLastUpdated(new Date());
    } catch (err) {
      setError("Failed to load scores. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadScores();
  };

  const handleCategoryChange = (event, newValue) => {
    if (categorySheets[newValue].link) {
      window.open(categorySheets[newValue].link, "_blank");
    } else {
      setSelectedCategory(newValue);
    }
  };

  // Convert Google Sheets URL to embeddable format
  const getEmbedUrl = (url) => {
    return url;
  };

  return (
    <Box
      sx={{
        width: "100%",
        padding: { xs: "30px 20px", sm: "40px 60px", lg: "50px 120px" },
        maxWidth: "1600px",
        margin: "auto",
        minHeight: "80vh",
      }}
    >
      <Typography
        variant={"h1"}
        sx={{
          textAlign: "center",
          fontSize: { xs: "35px", sm: "45px" },
          fontWeight: "500",
          color: "primary.darker",
        }}
      >
        Competition Scores
      </Typography>
      <Box
        sx={{
          height: "3px",
          maxWidth: { xs: "100px", md: "150px" },
          width: "100%",
          backgroundColor: "primary.main",
          margin: "auto",
          marginBottom: "30px",
        }}
      />

      {/* Header Section */}
      <Card sx={{ marginBottom: "30px" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <TableChartIcon
                sx={{ color: "primary.main", fontSize: "40px" }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ color: "primary.darker", fontWeight: "600" }}
                >
                  Live Scoreboard
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Real-time competition results and rankings
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
              {lastUpdated && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </Typography>
              )}
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  borderColor: "primary.main",
                  color: "primary.main",
                  "&:hover": {
                    borderColor: "primary.dark",
                    backgroundColor: "primary.light",
                    color: "white",
                  },
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Category Tabs */}
      <Paper sx={{ marginBottom: "0px" }}>
        <Tabs
          value={selectedCategory}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              fontWeight: 600,
              textTransform: "none",
              fontSize: "16px",
              minHeight: "60px",
            },
          }}
        >
          {categorySheets.map((category, index) => (
            <Tab
              key={index}
              label={category.name}
              sx={{
                color:
                  selectedCategory === index
                    ? "primary.main"
                    : "text.secondary",
                "&.Mui-selected": {
                  color: "primary.main",
                  fontWeight: 700,
                },
              }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Google Sheet Iframe */}
      <Card>
        <CardContent sx={{ padding: 0 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "60px",
              }}
            >
              <CircularProgress size={60} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ margin: "20px" }}>
              {error}
            </Alert>
          ) : (
            <Box sx={{ position: "relative", width: "100%", height: "80vh" }}>
              {/* <Typography
                variant="h6"
                sx={{
                  padding: "20px 20px 10px 20px",
                  color: "primary.darker",
                  fontWeight: "600",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                {categorySheets[selectedCategory].name} -{" "}
                {categorySheets[selectedCategory].description}
              </Typography> */}
              <iframe
                src={getEmbedUrl(categorySheets[selectedCategory].sheetUrl)}
                width="100%"
                height="700px"
                frameBorder="0"
                style={{
                  border: "none",
                  borderRadius: "0 0 8px 8px",
                }}
                title={`${categorySheets[selectedCategory].name} Scores`}
                loading="lazy"
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ScoresTemplate;
